import { memo, useCallback, useMemo } from 'react';

import { Breadcrumbs, type BreadcrumbsItemType } from '@air/component-breadcrumbs';

import { usePublicShowcaseCurrentSubPage } from '~/components/v2/components/showcase/hooks/usePublicShowcaseCurrentSubPage';
import {
  PUBLIC_SHOWCASE_NAV_HOME_ID,
  PUBLIC_SHOWCASE_NAV_HOME_LABEL,
} from '~/components/v2/components/showcase/publicShowcaseNavEntries';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

/**
 * Breadcrumbs for a collection sub-page: `Home / {Page Name}`, with Home linking back to the
 * link's root page and the current page as plain, non-clickable text. Renders nothing on the root
 * page — the root shows no breadcrumbs, unchanged. (A drilled-into board renders its own trail via
 * `PublicBoardTopbar`; this component only ever sees the collection page's own routes.)
 *
 * Fully smart: it reads its own context, so the page threads nothing.
 */
export const PublicShowcaseSubPageBreadcrumbs = memo(() => {
  const currentSubPage = usePublicShowcaseCurrentSubPage();
  const { onSelectPage } = usePublicShowcaseContext();

  // The Home crumb returns to the link's root page — however this surface navigates (a route push
  // on the URL surfaces, the editor's active-page state in the Edit tab). Mirrors
  // PublicShowcaseNavLink / PublicShowcaseNavMenuItem's internal-entry handling.
  const handleGoHome = useCallback(() => onSelectPage(null), [onSelectPage]);

  const items = useMemo<BreadcrumbsItemType[]>(() => {
    if (!currentSubPage) {
      return [];
    }

    return [
      {
        id: PUBLIC_SHOWCASE_NAV_HOME_ID,
        label: PUBLIC_SHOWCASE_NAV_HOME_LABEL,
        className: 'max-w-[170px]',
        onClick: handleGoHome,
        type: 'button',
      },
      {
        id: `sub-page-${currentSubPage.slug}`,
        label: currentSubPage.subPage.name,
        className: 'max-w-[350px]',
        type: 'item',
      },
    ];
  }, [currentSubPage, handleGoHome]);

  if (items.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs
      className="px-4 pt-3"
      data-testid="PUBLIC_COLLECTION_SUB_PAGE_BREADCRUMBS"
      items={items}
      size="small"
      visibleItemsAtEnd={2}
    />
  );
});

PublicShowcaseSubPageBreadcrumbs.displayName = 'PublicShowcaseSubPageBreadcrumbs';
